<template>
  <div class="login-password-form">
    <h3 class="mb-l">
      {{ $t("title.password") }}
    </h3>
    <div>
      <p>{{ $t('title.loginHint')}}</p>
      <b class="mr-2xs text-black">{{ this.email }}</b>
      <bf-link underline><a
        href="#changeAccount"
        @click.prevent="changeAccount"
      >{{ $t("common.changeAccount") }}</a></bf-link>
    </div>
    <bf-form
      ref="form"
      class="mt-xs lastpass-area"
      :model="form"
      :rules="formRules"
      @submit.prevent.native="checkEnter()"
    >
      <bf-form-item
        prop="password"
        :label="$t('form.enterYour', { name: $t('form.password') })"
      >
        <bf-input
          ref="input"
          :key="`pass-${count}`"
          :disabled="isLoading"
          type="password"
          autocomplete="on"
          name="password"
          showPassword
          v-model="form.password"
          class="w-100"
          :placeholder="$t('form.enterYour', { name: $t('form.password') })"
          @keyup.enter.native="onSubmit"
        >
          <template slot="append">
            <bf-button :loading="isLoading" type="primary" @click.native.stop.prevent="onSubmit">
              <bf-icon name="arrow-forward" />
            </bf-button>
          </template>
        </bf-input>
      </bf-form-item>
    </bf-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { setToken } from '@/utils/common';
import loginService from '@/services/login';
import userServices from '@/services/user';
import { reCaptcha, hideReCaptcha, showReCaptcha } from '@/utils/reCaptcha';

export default {
  name: 'LoginChallenge',
  props: {
    email: {
      type: String,
      default: '',
    },
    password: {
      type: String,
      default: '',
    },
    count: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      apiTempToken: process.env.VUE_APP_API_TEMPORARY_TOKEN_KEY || 'tmp_token',
      cookieTempToken: process.env.VUE_APP_COOKIE_TEMPORARY_TOKEN_KEY || 'tmp_token',
      apiAccessToken: process.env.VUE_APP_API_ACCESS_TOKEN_KEY || 'access_token',
      isLoading: false,
      form: {
        password: '',
      },
    };
  },
  computed: {
    ...mapGetters('device', [
      'isMobile',
    ]),
    ...mapGetters('auth', [
      'callbackUrl',
    ]),
    formRules() {
      return {
        password: [
          { required: true, message: this.$t('form.pleaseInput', { name: this.$t('title.password') }), trigger: ['change'] },
          {
            max: 255,
            message: this.$t('form.validator.max', { field: this.$t('title.password'), max: '255' }),
            trigger: ['change'],
          },
        ],
      };
    },
  },
  watch: {
    count(nextValue, oldValue) {
      if (nextValue !== oldValue) {
        this.$refs.input?.$el?.querySelector('input').focus();
        this.showPassword = false;
        this.form.password = this.password;
      }
    },
  },
  mounted() {
    if (!this.email) {
      this.$emit('goToStep', 'step-1');
    }
    this.$refs?.input?.$el?.querySelector('input').focus();
    showReCaptcha();
  },
  destroyed() {
    hideReCaptcha();
  },
  methods: {
    ...mapActions('auth', ['updateTicket', 'updateTwebmail']),
    changeAccount() {
      this.$refs?.form.resetFields();
      this.$emit('updateEmail', '');
      this.$emit('updatePassword', '');
      this.$emit('goToStep', 'step-1');
    },
    async onSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          this.$emit('updatePassword', this.form.password);
          try {
            this.isLoading = true;
            const reCaptchaToken = await reCaptcha();
            const payload = {
              email: this.email,
              password: this.form.password,
              service: this.callbackUrl || '',
            };
            if (this.$route?.query?._t && this.$route.query._t === 'webmail') {
              payload.t = 'webmail';
            }
            const response = await loginService.login(payload, reCaptchaToken);
            const isOtpEnabled = response?.data?.otp_enabled ?? 'false';
            // Get verified_phone
            const { verified_phone: verifiedPhone } = response?.data;
            this.$emit('updateVerifiedPhone', verifiedPhone);
            // Set ticket
            if (response?.data?.ticket) {
              this.updateTicket(response?.data?.ticket);
            }
            if (response?.data?.login_param) {
              this.updateTwebmail(response?.data?.login_param);
            }
            if (!isOtpEnabled) {
              // OTP ENABLE: FALSE
              setToken(response?.data && response.data[this.apiAccessToken]);
              this.getUserInfo();
            } else {
              // OTP ENABLE: TRUE
              // Set token
              const tempToken = response?.data && response?.data[this.apiTempToken];
              setToken(tempToken, this.cookieTempToken);
              // get otp viva email
              this.$emit('goToStep', 'otp-step');
              await new Promise((r) => setTimeout(r, 300));
              this.isLoading = false;
            }
          } catch (error) {
            this.isLoading = false;
          }
        }
      });
    },
    async getUserInfo() {
      try {
        await userServices.userInfo();
        this.$emit('goToStep', 'final-step');
      } catch (error) {
        const errorCode = error?.response?.data?.error;
        if (['NeedChangePasswordException', 'PasswordExpired'].includes(errorCode)) {
          this.$emit('updateMessage', error?.response?.data?.detail);
          this.$emit('goToStep', 'change-password-step');
        }
        await new Promise((r) => setTimeout(r, 300));
        this.isLoading = false;
      }
    },
    checkEnter() {
      return false;
    },
  },
};

</script>
